import React from 'react';
import { Link } from 'react-router-dom';
import { ExportOutlined } from '@ant-design/icons';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ContactAvatarWithNameEmail } from '../contact_avatar_with_name_email/ContactAvatarWithNameEmail';
import RefrigerantEventTypeDisplay from './RefrigerantEventTypeDisplay';
import {
	formatEventCertification,
	RefrigerantEventCertificationInfo,
	RefrigerantEventDate,
} from './RefrigerantEventCells';

interface LedgerColumnOptions {
	userType: string;
	/** Off on the asset detail tab, where every row is the same appliance. */
	showAsset?: boolean;
	/**
	 * The technician's 608 certification, as snapshotted on the event, in a COLUMN of its own. Only the
	 * servicing record does that: it is what makes that document evidence rather than a list, and it
	 * would be dead weight on the working ledger, where the same tech recurs on every row.
	 *
	 * The working ledger still carries the same facts, through the (i) beside the technician's name —
	 * see `RefrigerantEventCertificationInfo`. One row at a time on request, rather than a column of
	 * near-identical certificate numbers.
	 */
	showCertification?: boolean;
	/**
	 * Renders for PAPER rather than for the UI: plain event text instead of a coloured pill, and a
	 * plain technician name instead of an avatar. One flag rather than two, because a row that mixed
	 * the two treatments would read as neither a list nor a document.
	 */
	printable?: boolean;
}

/**
 * The servicing-record columns, shared by the Event Log screen, an appliance's own ledger and the
 * printable servicing record, so the three can never drift on what a 608 record looks like.
 *
 * Column order follows the design: Date, Event, Tech, WO, Notes. Tech sits before the work order
 * because the person is the fact an auditor scans for — the WO is a cross-reference.
 *
 * Dates render date-only. An `occurredAt` is the day the work happened on a compliance record, and
 * rendering it in the viewer's zone would let a UTC-midnight event show as the previous day — which
 * on a record whose whole purpose is "when was this appliance serviced" is a wrong answer, not a
 * cosmetic one.
 */
export const getRefrigerantEventLedgerColumns = ({
	userType,
	showAsset = true,
	showCertification = false,
	printable = false,
}: LedgerColumnOptions) =>
	[
		{
			title: 'Date',
			dataIndex: 'occurredAt',
			width: 110,
			render: (occurredAt) => <RefrigerantEventDate occurredAt={occurredAt} />,
		},
		...(showAsset
			? [
					{
						title: 'Asset',
						dataIndex: 'assetId',
						width: 280,
						render: (assetId, record) => {
							if (!assetId) return <span className="text-character-secondary">--</span>;
							/*
							  The design's asset cell: the appliance's name, its TYPE beside it in secondary
							  weight, then `Asset ID … · Serial #…` underneath — plus the location, which the
							  ledger needs and the appliance's own page does not (an auditor reading the whole
							  tenant's record has to know WHERE, and two stores routinely hold identically
							  named equipment).

							  Every field comes off the `asset` the server hydrates onto each listed event
							  (one batched query per page — RefrigerantEventServiceImpl.hydrateAll). If that
							  hydration is missing the cell degrades to the bare `Asset #<id>` link, which is
							  also the correct rendering for the one case hydration cannot cover: an event on a
							  since-deleted appliance, which the ledger keeps because the servicing record is
							  the whole point.

							  `Asset ID` is the asset's `id`, matching what the asset detail page's identity
							  panel labels "Asset ID" — not `assetNumber`, which that panel calls "Asset
							  Number".
							*/
							const name = nullSafeGet('asset.name', record);
							const assetTypeName = nullSafeGet('asset.assetTypeName', record);
							const serialNumber = nullSafeGet('asset.serialNumber', record);
							const locationName = nullSafeGet('asset.locationName', record);
							// Rendered only when the appliance actually hydrated. `Asset ID <id>` is knowable
							// without it, but printing it alone under an `Asset #<id>` fallback link says the
							// same number twice and reads as the only detail the cell has — which is exactly
							// how a missing hydration disguised itself as a half-built cell.
							const detailLine = nullSafeGet('asset', record)
								? [`Asset ID ${assetId}`, serialNumber && `Serial #${serialNumber}`, locationName]
										.filter(Boolean)
										.join(' • ')
								: null;

							return (
								<div className="flex flex-col">
									<div className="flex flex-row flex-wrap items-baseline gap-2">
										{/* Dark and medium-weight, not link-blue, matching the design and the same
										    asset-identity block the log-event drawer renders for a locked appliance.
										    Still a Link — the row is the way into the appliance's own ledger. */}
										<Link
											to={`/${userType}/assets/detail/${assetId}/refrigerantTracking`}
											onClick={(e) => e.stopPropagation()}
											className="bodymedium text-character-primary"
										>
											{name || `Asset #${assetId}`}
										</Link>
										{assetTypeName ? (
											<span className="footnoteregular text-character-secondary">
												{assetTypeName}
											</span>
										) : null}
									</div>
									{detailLine ? (
										<span className="footnoteregular text-character-secondary">{detailLine}</span>
									) : null}
								</div>
							);
						},
					},
			  ]
			: []),
		{
			title: showCertification ? 'Event Details' : 'Event',
			dataIndex: 'eventType',
			width: 220,
			render: (_, record) => <RefrigerantEventTypeDisplay event={record} plain={printable} />,
		},
		{
			title: 'Tech',
			dataIndex: 'technicianEmail',
			width: 220,
			render: (technicianEmail, record) => {
				// Falls back to whoever authored the row. On an unplanned top-off the tech and the author
				// are the same person and only one of the two fields is filled in.
				const contact =
					nullSafeGet('technicianContact', record) || nullSafeGet('createdByContact', record);
				const contactName = `${nullSafeGet('nameGiven', contact) || ''} ${
					nullSafeGet('nameFamily', contact) || ''
				}`.trim();
				// The event's OWN name SNAPSHOT outranks both contacts, for the same reason
				// `formatEventCertification` reads the event's certification snapshot: the row has to name
				// the person its credential attests to. The server writes the snapshot on either path —
				// the picked technician's contact name as of the event, or the free text captured beside
				// the 608 card of a certified third party who has no login here. On that second path
				// `technicianContact` is empty and `createdByContact` is merely whoever FILED the row, so
				// attributing by contact prints the wrong name against a real certificate. Absent on rows
				// logged before the field existed, which still attribute by contact.
				const snapshotName = nullSafeGet('technicianName', record);
				const fallbackEmail = technicianEmail || nullSafeGet('createdBy', record);
				// The avatar, and the email under it, belong to the CONTACT — so they are shown only while
				// the name on the row is that contact's. For the third party they would put the filer's
				// face and address beside somebody else's name, which is the misattribution this column
				// exists to avoid, so their name stands on its own.
				const isThirdPartyTechnician = Boolean(snapshotName) && snapshotName !== contactName;
				// The (i) carrying the event's certification snapshot. Suppressed on the printable record,
				// where the same facts already have a Certification COLUMN — a popover is not reachable on
				// paper, and an icon printed beside a name that leads nowhere is noise on a document whose
				// value is that it can be read literally.
				const certificationInfo = printable ? null : (
					<RefrigerantEventCertificationInfo event={record} />
				);
				if (printable || isThirdPartyTechnician) {
					return (
						<div className="flex flex-row items-center gap-1">
							<span className="bodyregular text-character-primary">
								{snapshotName || contactName || fallbackEmail || '--'}
							</span>
							{certificationInfo}
						</div>
					);
				}
				return (
					<div className="flex flex-row items-center gap-1">
						<ContactAvatarWithNameEmail
							size="small"
							contact={contact}
							fallbackEmail={fallbackEmail}
						/>
						{certificationInfo}
					</div>
				);
			},
		},
		...(showCertification
			? [
					{
						title: 'Certification',
						dataIndex: 'certificationNumber',
						width: 180,
						render: (_, record) => (
							<span className="bodyregular text-character-primary">
								{formatEventCertification(record)}
							</span>
						),
					},
			  ]
			: []),
		{
			title: showCertification ? 'Work Order' : 'WO',
			dataIndex: 'workOrderId',
			width: 150,
			render: (workOrderId) =>
				workOrderId ? (
					<Link
						to={`/${userType}/workOrders/detail/${workOrderId}/details`}
						onClick={(e) => e.stopPropagation()}
						className="bodyregular"
					>
						#WO {workOrderId} <ExportOutlined />
					</Link>
				) : (
					<span className="text-character-secondary">--</span>
				),
		},
		{
			title: 'Notes',
			dataIndex: 'notes',
			render: (notes) =>
				notes ? (
					<span className="bodyregular text-character-primary">{notes}</span>
				) : (
					<span className="text-character-secondary">--</span>
				),
		},
	].filter(Boolean);

export default getRefrigerantEventLedgerColumns;
